import React, { useState } from "react";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import SyncAltRoundedIcon from "@mui/icons-material/SyncAltRounded";
import { SiteFooter, SiteHeader } from "../components/SiteChrome";
import useScrollReveal from "../hooks/useScrollReveal";
import "./SupportPage.css";

const categories = [
  {
    icon: <HelpOutlineRoundedIcon />,
    title: "Getting Started",
    copy: "New to trilLOAN? Learn the basics and get up and running quickly.",
  },
  {
    icon: <AccountCircleOutlinedIcon />,
    title: "Account & Verification",
    copy: "Everything about your account, profile, and verification process.",
  },
  {
    icon: <AccountBalanceWalletOutlinedIcon />,
    title: "Borrowing & Lending",
    copy: "Learn how to borrow money or lend to others on our platform.",
  },
  {
    icon: <SyncAltRoundedIcon />,
    title: "Payment & Transfers",
    copy: "Manage your money, payments, and transfers safely.",
  },
  {
    icon: <SecurityRoundedIcon />,
    title: "Security & Privacy",
    copy: "Keeping your account and personal information safe and secure.",
  },
  {
    icon: <ChatBubbleOutlineRoundedIcon />,
    title: "Troubleshooting",
    copy: "Quick fixes for common issues and technical problems.",
  },
];

const faqs = [
  {
    question: "What is trilLOAN?",
    answer:
      "trilLOAN is a mobile financial platform for loans, payments, transfers, and related services in the Philippines.",
  },
  {
    question: "Where can I download the trilLOAN app?",
    answer:
      "You can download the trilLOAN app from the Apple App Store and Google Play Store.",
  },
  {
    question: "Does trilLOAN have a wallet?",
    answer:
      "trilLOAN does not maintain a digital wallet balance. Users can link supported bank accounts, cards, and payment methods for transactions.",
  },
  {
    question: "Which devices are compatible with trilLOAN?",
    answer:
      "The app supports Android devices running Android 6.0 and above, and iPhones running iOS 12.10 and above.",
  },
  {
    question: "How do I create a trilLOAN account?",
    answer:
      "Download the app, enter your mobile number, verify your OTP, provide your personal information, set your password and MPIN, then accept the terms.",
  },
  {
    question: "Which valid IDs are accepted for account verification?",
    answer:
      "Primary IDs include Philippine driver's license, passport, UMID, SSS, PRC, postal, voter, national, and PhilHealth IDs. Secondary IDs may be accepted for registered-level upgrades.",
  },
  {
    question: "Why was my account upgrade rejected?",
    answer:
      "Common reasons include mismatched personal details, expired or unsupported IDs, blurry ID/selfie images, or not meeting minimum age requirements.",
  },
  {
    question: "Can't find the answer you're looking for?",
    answer:
      "Contact trilLOAN Support in the app, through live chat, by email at support@trilloan.com, or by phone at 02-7005-3458.",
  },
];

function AccordionItem({ item, index }) {
  const [open, setOpen] = useState(index === 0);

  return (
    <article className="support-accordion tl-reveal">
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span>{item.question}</span>
        <ExpandMoreRoundedIcon className={open ? "is-open" : ""} />
      </button>
      {open && <p>{item.answer}</p>}
    </article>
  );
}

export default function FAQs() {
  useScrollReveal();

  return (
    <main className="support-page">
      <SiteHeader />
      <section className="support-hero tl-reveal is-visible">
        <p>Help Center</p>
        <h1>Find answers, learn how to use trilLOAN, and get the support you need.</h1>
        <div className="support-search">
          <SearchRoundedIcon />
          <span>Search for help articles, features, or common questions...</span>
        </div>
      </section>

      <section className="support-section tl-reveal">
        <div className="support-section-heading">
          <p>Browse by Category</p>
          <h2>Start with the topic that matches your question</h2>
        </div>
        <div className="support-category-grid">
          {categories.map((category, index) => (
            <article className={`support-category tl-reveal tl-reveal-delay-${index % 3}`} key={category.title}>
              <span>{category.icon}</span>
              <div>
                <h3>{category.title}</h3>
                <p>{category.copy}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="support-section support-faq tl-reveal">
        <div className="support-section-heading">
          <p>Popular Questions</p>
          <h2>Quick answers to common trilLOAN questions</h2>
        </div>
        <div className="support-accordion-list">
          {faqs.map((item, index) => (
            <AccordionItem item={item} index={index} key={item.question} />
          ))}
        </div>
      </section>

      <section className="support-contact tl-reveal">
        <article>
          <PaymentsOutlinedIcon />
          <h2>Still need help?</h2>
          <p>Browse support topics or contact our team for account-specific concerns.</p>
          <a href="mailto:support@trilloan.com">Email Support</a>
        </article>
        <article>
          <ChatBubbleOutlineRoundedIcon />
          <h2>Contact Support</h2>
          <p>Our support team is available to help with questions and concerns.</p>
          <a href="/#download">Open App</a>
        </article>
      </section>
      <SiteFooter />
    </main>
  );
}
